import { Link } from '../../primitives/Link/Link';
import type { MouseEventHandler, ReactNode } from 'react';
import styles from './BreadcrumbButton.module.css';

/**
 * Figma: `_Breadcrumb button base` (200:2495) — one crumb. Variants: `Current`
 * (False | True) × `State` (Default | Hover). 14px tall, no padding, no fill and
 * no radius; the whole component is a line of text whose colour carries the
 * state.
 *
 * `Current` is what the crumb *is* — the page you are on — not a style choice,
 * and it is the last crumb by definition, so `Breadcrumbs` derives it rather
 * than taking it. Hover is CSS. The current crumb has no hover variant of its
 * own: Figma binds `text/primary` for both its states, which is the design
 * saying it is not a link.
 *
 * The `_` prefix marks it internal in Figma, so it is not part of the public
 * package surface — compose it through `Breadcrumbs`.
 */
export type BreadcrumbButtonState = 'default' | 'hover';

export type BreadcrumbButtonProps = {
  children: ReactNode;
  /** Figma `Current=True`. */
  current?: boolean;
  href?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  /**
   * Normally leave this alone: hover is CSS and `current` is positional.
   * Forcing a state is for documentation and visual regression, where all four
   * variants need to render side by side.
   */
  state?: BreadcrumbButtonState;
  className?: string;
};

export function BreadcrumbButton({
  children,
  current = false,
  href,
  onClick,
  state,
  className,
}: BreadcrumbButtonProps) {
  const interactive = href !== undefined || onClick !== undefined;

  const classes = [
    styles.crumb,
    current ? styles.current : styles.link,
    /*
     * Hover is live only when nothing is forced and there is something to
     * click, so `:hover` reacts in the app and stays inert in a states story.
     */
    state === 'hover' ? styles.hover : null,
    interactive && state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /*
   * `aria-current="page"` is not in Figma — Figma has no concept of which page
   * you are on, only of a colour. It is the one thing that makes a breadcrumb
   * legible to a screen reader.
   */
  const currentProps = current ? ({ 'aria-current': 'page' } as const) : {};
  const content = <span className={styles.label}>{children}</span>;

  if (href !== undefined) {
    return (
      <Link className={classes} href={href} onClick={onClick} {...currentProps}>
        {content}
      </Link>
    );
  }

  /*
   * The current crumb defaults to plain text rather than a control: the design
   * gives it no hover feedback, which is how it says "you are already here".
   * A caller who passes `href` or `onClick` still gets a real element.
   */
  if (!interactive) {
    return (
      <span className={classes} {...currentProps}>
        {content}
      </span>
    );
  }

  return (
    <button className={classes} type="button" onClick={onClick} {...currentProps}>
      {content}
    </button>
  );
}
